import { TFunction } from '@psyomics/ui-common';
import { CellDateFormatter, ColumnDef, useFormatCellAsDate } from '@psyomics/ui-components';
import { useTranslation } from 'react-i18next';

import { ReferralUploadJobResult } from './types';

function fileNameColumn(t: TFunction): ColumnDef<ReferralUploadJobResult> {
  return {
    accessorKey: 'fileName',
    cell: (info) => info.getValue(),
    enableSorting: false,
    header: t('page.referralUploadJobs.table.fileName'),
    id: 'fileName',
  };
}

function serviceColumn(t: TFunction): ColumnDef<ReferralUploadJobResult> {
  return {
    accessorFn: (job) => job.service.description,
    cell: (info) => info.getValue(),
    enableSorting: false,
    header: t('page.referralUploadJobs.table.service'),
    id: 'service',
  };
}

function rowCountColumn(t: TFunction): ColumnDef<ReferralUploadJobResult> {
  return {
    accessorKey: 'rowCount',
    cell: (info) => info.getValue(),
    enableSorting: false,
    header: t('page.referralUploadJobs.table.rowCount'),
    id: 'rowCount',
  };
}

function completeCountColumn(t: TFunction): ColumnDef<ReferralUploadJobResult> {
  return {
    accessorFn: (job) => job.statusCounts.created,
    cell: (info) => info.getValue(),
    enableSorting: false,
    header: t('page.referralUploadJobs.table.complete'),
    id: 'completeCount',
  };
}

function pendingCountColumn(t: TFunction): ColumnDef<ReferralUploadJobResult> {
  return {
    accessorFn: (job) => job.statusCounts.pending,
    cell: (info) => info.getValue(),
    enableSorting: false,
    header: t('page.referralUploadJobs.table.pending'),
    id: 'pendingCount',
  };
}

function erroredCountColumn(t: TFunction): ColumnDef<ReferralUploadJobResult> {
  return {
    accessorFn: (job) => job.statusCounts.errored,
    cell: (info) => info.getValue(),
    enableSorting: false,
    header: t('page.referralUploadJobs.table.errored'),
    id: 'erroredCount',
  };
}

function createdByColumn(t: TFunction): ColumnDef<ReferralUploadJobResult> {
  return {
    accessorKey: 'createdBy',
    cell: (info) => info.getValue(),
    enableSorting: false,
    header: t('page.referralUploadJobs.table.uploadedBy'),
    id: 'createdBy',
  };
}

function createdAtColumn(
  dateFormatter: CellDateFormatter<ReferralUploadJobResult>,
  t: TFunction
): ColumnDef<ReferralUploadJobResult> {
  return {
    accessorKey: 'createdAt',
    cell: dateFormatter,
    enableSorting: false,
    header: t('page.referralUploadJobs.table.uploadedAt'),
    id: 'createdAt',
  };
}

export const useTableColumns = (): ColumnDef<ReferralUploadJobResult>[] => {
  const { t } = useTranslation();
  const dateFormatter = useFormatCellAsDate<ReferralUploadJobResult>(t, 'datetime.short');

  return [
    serviceColumn(t),
    fileNameColumn(t),
    rowCountColumn(t),
    completeCountColumn(t),
    pendingCountColumn(t),
    erroredCountColumn(t),
    createdByColumn(t),
    createdAtColumn(dateFormatter, t),
  ];
};
